import { useEffect, useRef } from 'react';
import { useGame } from '../contexts/GameContext';
import type { CardSetType } from '../types/game';
import { playCue } from './foley';

interface TableSnapshot {
  otherPlayers: number;
  otherVotes: number;
  votesRevealed: boolean;
  cardSet: CardSetType;
}

/**
 * Sounds the table's real-time events: teammates arriving, votes landing, the
 * reveal, the next round. Your own clicks are handled by the `data-foley-*`
 * attributes on the controls themselves — these are the things other people do.
 *
 * Call once from the game page.
 */
export const useGameSounds = (): void => {
  const { players, currentPlayerId, votesRevealed, cardSet, connectionStatus } = useGame();

  const isSeated = Boolean(currentPlayerId) && connectionStatus === 'connected';
  // null while away from the table, so re-joining announces the seat rather
  // than replaying every teammate and vote already in the state we receive.
  const previous = useRef<TableSnapshot | null>(null);
  const wasConnected = useRef(false);

  useEffect(() => {
    if (!isSeated) {
      previous.current = null;
      return;
    }

    const others = players.filter((player) => player.id !== currentPlayerId);
    const snapshot: TableSnapshot = {
      otherPlayers: others.length,
      otherVotes: others.filter((player) => player.hasVoted).length,
      votesRevealed,
      cardSet,
    };

    const prev = previous.current;
    previous.current = snapshot;

    if (!prev) {
      playCue('ready');
      return;
    }

    if (snapshot.otherPlayers > prev.otherPlayers) {
      playCue('ping');
    } else if (snapshot.otherPlayers < prev.otherPlayers) {
      playCue('drop');
    }

    // Card set is checked first because changing the deck also clears the round
    // server-side: both arrive in one gameState, and "the deck changed" is the
    // more useful thing to hear. A reveal never coincides with it — the reset
    // only ever moves votesRevealed true -> false — so `complete` still wins
    // whenever it applies.
    if (snapshot.cardSet !== prev.cardSet) {
      playCue('switch');
    } else if (snapshot.votesRevealed !== prev.votesRevealed) {
      playCue(snapshot.votesRevealed ? 'complete' : 'swoosh');
    } else if (snapshot.otherVotes > prev.otherVotes) {
      // Deliberately not `tick`: the cooldown Foley applies is keyed by cue
      // name, so sharing one with the card picker's hover would let a pointer
      // sweep swallow a teammate's vote (and vice versa).
      playCue('latch');
    }
  }, [players, currentPlayerId, votesRevealed, cardSet, isSeated]);

  useEffect(() => {
    const connected = connectionStatus === 'connected';
    if (connected === wasConnected.current) {
      return;
    }

    wasConnected.current = connected;
    if (!connected) {
      playCue('error');
    }
  }, [connectionStatus]);
};
